const progressiveSelector = '[data-progressive-image], [data-project-gallery-image]';
const progressiveShellSelector = '[data-progressive-image-shell], [data-project-gallery-image-shell]';

function getProgressiveShell(image) {
  return image.closest(progressiveShellSelector);
}

function getResponsiveSrcset(image, sourceUrl) {
  const existingSrcset = image.dataset.srcset || '';

  if (!image.matches('[data-project-gallery-image]')) {
    return existingSrcset;
  }

  const sourceWidth = Number.parseInt(image.getAttribute('width') || '', 10);

  if (!sourceUrl || !Number.isFinite(sourceWidth) || sourceWidth <= 0) {
    return existingSrcset;
  }

  const sourceAlreadyPresent = existingSrcset
    .split(',')
    .some((candidate) => candidate.trim().startsWith(`${sourceUrl} `));

  if (sourceAlreadyPresent) {
    return existingSrcset;
  }

  const sourceCandidate = `${sourceUrl} ${sourceWidth}w`;
  return existingSrcset ? `${existingSrcset}, ${sourceCandidate}` : sourceCandidate;
}

function revealProgressiveImage(image) {
  const shell = getProgressiveShell(image);

  if (!shell) {
    return;
  }

  const reveal = () => {
    shell.classList.add('is-loaded');
    shell.classList.remove('is-loading');
  };

  if (typeof image.decode === 'function') {
    image.decode().catch(() => {}).finally(reveal);
    return;
  }

  reveal();
}

function hydrateProgressiveImage(image) {
  if (image.dataset.progressiveHydrated === 'true') {
    return;
  }

  const sourceUrl = image.dataset.src;

  if (!sourceUrl) {
    return;
  }

  image.dataset.progressiveHydrated = 'true';

  const shell = getProgressiveShell(image);
  if (shell) {
    shell.classList.add('is-loading');
  }

  image.addEventListener('load', () => revealProgressiveImage(image), {once: true});
  image.addEventListener('error', () => {
    if (shell) {
      shell.classList.remove('is-loading');
    }
  }, {once: true});

  if (image.dataset.sizes) {
    image.sizes = image.dataset.sizes;
  }

  const responsiveSrcset = getResponsiveSrcset(image, sourceUrl);
  if (responsiveSrcset) {
    image.srcset = responsiveSrcset;
  }

  image.src = sourceUrl;
  image.removeAttribute('data-src');
  image.removeAttribute('data-srcset');
  image.removeAttribute('data-sizes');
}

function queueProgressiveImage(image) {
  if (
    image.dataset.progressiveHydrated === 'true'
    || image.dataset.progressiveQueued === 'true'
  ) {
    return;
  }

  // Make sure the tiny placeholder is decoded before swapping to the real
  // responsive source so the user never sees an empty media frame.
  image.loading = 'eager';

  if (image.complete && image.naturalWidth > 0) {
    hydrateProgressiveImage(image);
    return;
  }

  image.dataset.progressiveQueued = 'true';

  const continueToHighResolution = () => {
    image.removeAttribute('data-progressive-queued');
    hydrateProgressiveImage(image);
  };

  image.addEventListener('load', continueToHighResolution, {once: true});
  image.addEventListener('error', continueToHighResolution, {once: true});
}

export function progressiveImages(root = document) {
  const context = root || document;
  const images = [];

  if (context.nodeType === 1 && context.matches(progressiveSelector)) {
    images.push(context);
  }

  if (context.querySelectorAll) {
    images.push(...context.querySelectorAll(progressiveSelector));
  }

  if (!images.length) {
    return;
  }

  const mobileViewport = window.matchMedia('(max-width: 767px)');
  const saveData = Boolean(navigator.connection && navigator.connection.saveData);
  const rootMargin = saveData
    ? '120px 0px'
    : (mobileViewport.matches ? '320px 0px' : '600px 0px');

  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        queueProgressiveImage(entry.target);
        observer.unobserve(entry.target);
      });
    }, {
      rootMargin,
      threshold: 0,
    });

    images.forEach((image) => observer.observe(image));
    return;
  }

  const checkImages = () => {
    const preloadDistance = saveData ? 120 : (mobileViewport.matches ? 320 : 600);

    images.forEach((image) => {
      if (image.dataset.progressiveHydrated === 'true') {
        return;
      }

      const rect = image.getBoundingClientRect();

      if (rect.top < window.innerHeight + preloadDistance && rect.bottom > -preloadDistance) {
        queueProgressiveImage(image);
      }
    });
  };

  checkImages();
  window.addEventListener('scroll', checkImages, {passive: true});
  window.addEventListener('resize', checkImages, {passive: true});
}
